import React from 'react'
import { getTabId } from 'utils/common'

export type PopupBroadcastApi = {
  checkAnyPopupOpen: () => Promise<boolean>
}

export const usePopupBroadcast = (isPopupOpen: boolean): PopupBroadcastApi => {
  const bcRef = React.useRef<BroadcastChannel | null>(null)

  React.useEffect(() => {
    try {
      bcRef.current = new BroadcastChannel('visit-popup-channel')
    } catch (e) {
      bcRef.current = null
    }
    return () => {
      try {
        bcRef.current?.close()
      } catch (e) {
        console.error('Failed to close BroadcastChannel', e)
      }
      bcRef.current = null
    }
  }, [])

  React.useEffect(() => {
    const bc = bcRef.current
    if (!bc) return
    const handler = (ev: MessageEvent) => {
      const msg = ev.data
      if (msg?.type === 'status-request') {
        if (isPopupOpen) {
          bc.postMessage({ type: 'status-open', tabId: getTabId() })
        }
      }
    }
    bc.addEventListener('message', handler as any)
    return () => bc.removeEventListener('message', handler as any)
  }, [isPopupOpen])

  const checkAnyPopupOpen = React.useCallback((): Promise<boolean> => {
    const bc = bcRef.current
    if (!bc) return Promise.resolve(false) // No channel → cannot detect, proceed
    let blocked = false
    const handler = (ev: MessageEvent) => {
      const msg = ev.data
      if (msg?.type === 'status-open' && msg.tabId !== getTabId()) {
        blocked = true
      }
    }
    bc.addEventListener('message', handler as any)
    try {
      bc.postMessage({ type: 'status-request', tabId: getTabId() })
    } catch (e) {
      console.error('Failed to post message to BroadcastChannel', e)
    }
    return new Promise<boolean>((resolve) => {
      setTimeout(() => {
        bc.removeEventListener('message', handler as any)
        resolve(blocked)
      }, 400)
    })
  }, [])

  return { checkAnyPopupOpen }
}
